import React from "react";
import { observer } from "mobx-react";
import { MusicalWorkUpdateStore } from "../../store";
import { MusicalWorkUpdateForm } from "./MusicalWorkUpdateForm";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { OutdatedAfterSaveNotice } from "../../../../common/controls/forms/OutdatedAfterSaveNotice";

export type MusicalWorkUpdateProps = {
  musicalWorkUpdateStore: MusicalWorkUpdateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const MusicalWorkUpdate: React.FC<MusicalWorkUpdateProps> = observer(
  ({ musicalWorkUpdateStore, showToolbox = true, showSaveFooter }) => {
    return (
      <>
        <SavingOverlay open={musicalWorkUpdateStore.isSaving} />
        <OutdatedAfterSaveNotice
          open={musicalWorkUpdateStore.savedButOutdated}
          onReload={() => musicalWorkUpdateStore.reloadMusicalWork()}
          reloadDisabled={musicalWorkUpdateStore.isProcessing}
        />
        <MusicalWorkUpdateForm
          musicalWorkUpdateStore={musicalWorkUpdateStore}
          titleError={musicalWorkUpdateStore.errors.title?.message}
          onTitleChange={(value: string) => {
            musicalWorkUpdateStore.setTitleValue(value);
          }}
          title={musicalWorkUpdateStore.entityValues.title}
          musicalGenreError={
            musicalWorkUpdateStore.errors.musicalGenre?.message
          }
          onMusicalGenreChange={(value) => {
            musicalWorkUpdateStore.setMusicalGenreValue(value);
          }}
          musicalGenreSelectedValue={
            musicalWorkUpdateStore.entityValues.showMusicalGenre
          }
          onMusicalGenreSelected={(value) => {
            musicalWorkUpdateStore.setSelectedMusicalGenre(value);
          }}
          musicalGenreOptions={musicalWorkUpdateStore.getMusicalGenreOptions()}
          alternativeTitlesError={
            musicalWorkUpdateStore.errors.alternativeTitles?.message
          }
          onAlternativeTitlesChange={(value: string[]) => {
            musicalWorkUpdateStore.setAlternativeTitlesValue(value);
          }}
          alternativeTitles={
            musicalWorkUpdateStore.entityValues.alternativeTitles
          }
          workAuthorUpdateStore={musicalWorkUpdateStore.authorsUpdateStore}
          authorsVisibleColumns={musicalWorkUpdateStore.authorsVisibleColumns}
          authorsColumnTypes={musicalWorkUpdateStore.authorsColumnTypes}
          authorsOnRestore={(id: string) =>
            musicalWorkUpdateStore.authorsRestore(id)
          }
          authorsOnCommitChanges={(changes) =>
            musicalWorkUpdateStore.authorsCommitChanges(changes)
          }
          authorsOnCommitInlineChange={(rowId, changes) =>
            musicalWorkUpdateStore.authorsCommitInlineChange(rowId, changes)
          }
          authorsValidateChanges={(changes) =>
            musicalWorkUpdateStore.authorsValidateChanges(changes)
          }
          authorsRows={musicalWorkUpdateStore.entityValues.showAuthors}
          authorsError={musicalWorkUpdateStore.errors.authors?.message}
          notesError={musicalWorkUpdateStore.errors.notes?.message}
          onNotesChange={(value: string) => {
            musicalWorkUpdateStore.setNotesValue(value);
          }}
          notes={musicalWorkUpdateStore.entityValues.notes}
          performersError={musicalWorkUpdateStore.errors.performers?.message}
          onPerformersChange={(value) => {
            musicalWorkUpdateStore.setPerformersValue(value);
          }}
          performersSelectedValues={
            musicalWorkUpdateStore.entityValues.showPerformers
          }
          onPerformersSelected={(value) => {
            musicalWorkUpdateStore.setSelectedPerformers(value);
          }}
          performersOptions={musicalWorkUpdateStore.getPerformersOptions()}
          cmsIdError={musicalWorkUpdateStore.errors.cmsId?.message}
          onCmsIdChange={(value: string) => {
            musicalWorkUpdateStore.setCmsIdValue(value);
          }}
          cmsId={musicalWorkUpdateStore.entityValues.cmsId}
          territoriesError={musicalWorkUpdateStore.errors.territories?.message}
          onTerritoriesChange={(value) => {
            musicalWorkUpdateStore.setTerritoriesValue(value);
          }}
          territoriesSelectedValues={
            musicalWorkUpdateStore.entityValues.showTerritories
          }
          onTerritoriesSelected={(value) => {
            musicalWorkUpdateStore.setSelectedTerritories(value);
          }}
          territoriesOptions={musicalWorkUpdateStore.getTerritoriesOptions()}
          iswcError={musicalWorkUpdateStore.errors.iswc?.message}
          onIswcChange={(value: string) => {
            musicalWorkUpdateStore.setIswcValue(value);
          }}
          iswc={musicalWorkUpdateStore.entityValues.iswc}
          scoreOnRestoreFile={(id: string) =>
            musicalWorkUpdateStore.scoreRestoreFile(id)
          }
          scoreOnLoadFile={(file: any) =>
            musicalWorkUpdateStore.scoreUploadFile(file)
          }
          scoreOnCommitFile={(changes) =>
            musicalWorkUpdateStore.scoreCommitFile(changes)
          }
          scoreFiles={musicalWorkUpdateStore.entityValues.__score_files}
          scoreError={musicalWorkUpdateStore.errors.score?.message}
          phonogramUpdateStore={musicalWorkUpdateStore.phonogramsUpdateStore}
          phonogramsVisibleColumns={
            musicalWorkUpdateStore.phonogramsVisibleColumns
          }
          phonogramsColumnTypes={musicalWorkUpdateStore.phonogramsColumnTypes}
          phonogramsOnRestore={(id: string) =>
            musicalWorkUpdateStore.phonogramsRestore(id)
          }
          phonogramsOnCommitChanges={(changes) =>
            musicalWorkUpdateStore.phonogramsCommitChanges(changes)
          }
          phonogramsOnCommitInlineChange={(rowId, changes) =>
            musicalWorkUpdateStore.phonogramsCommitInlineChange(rowId, changes)
          }
          phonogramsValidateChanges={(changes) =>
            musicalWorkUpdateStore.phonogramsValidateChanges(changes)
          }
          phonogramsRows={musicalWorkUpdateStore.entityValues.showPhonograms}
          phonogramsError={musicalWorkUpdateStore.errors.phonograms?.message}
          originalLyricsError={
            musicalWorkUpdateStore.errors.originalLyrics?.message
          }
          onOriginalLyricsChange={(value: string) => {
            musicalWorkUpdateStore.setOriginalLyricsValue(value);
          }}
          originalLyrics={musicalWorkUpdateStore.entityValues.originalLyrics}
          contractObservationError={
            musicalWorkUpdateStore.errors.contractObservation?.message
          }
          onContractObservationChange={(value: string) => {
            musicalWorkUpdateStore.setContractObservationValue(value);
          }}
          contractObservation={
            musicalWorkUpdateStore.entityValues.contractObservation
          }
          createdAt_={musicalWorkUpdateStore.getCreatedAt_Value()}
          lastUpdatedAt_={musicalWorkUpdateStore.getLastUpdatedAt_Value()}
          dummyCRUDRestoredObservable={
            musicalWorkUpdateStore.dummyCRUDRestoredObservable
          }
          recordId={musicalWorkUpdateStore.musicalWorkInstance?.id || ""}
          showToolbox={showToolbox}
          showSaveFooter={showSaveFooter}
          saving={musicalWorkUpdateStore.saveDisabled}
          onSave={() => musicalWorkUpdateStore.saveMusicalWork()}
          onCancel={() => musicalWorkUpdateStore.cancelMusicalWork()}
          authorsForChart={musicalWorkUpdateStore.entityValues.showAuthors}
        />
        <ErrorDialog
          open={musicalWorkUpdateStore.errorDialogOpened}
          title={musicalWorkUpdateStore.errorTitle}
          description={musicalWorkUpdateStore.errorDescription}
          onClose={() => musicalWorkUpdateStore.closeErrorDialog()}
        />
        <ConfirmCancelDialog
          open={musicalWorkUpdateStore.confirmDialogOpened}
          title={musicalWorkUpdateStore.confirmDialogTitle}
          description={musicalWorkUpdateStore.confirmDialogDescription}
          onConfirm={() => musicalWorkUpdateStore.executeConfirmedAction()}
          onCancel={() => musicalWorkUpdateStore.closeConfirmDialog()}
        />
      </>
    );
  }
);
